import {
  NormalizedBusinessLogicRule,
  NormalizedStreamBusinessLogic
} from '@/types/businessLogic'
import { ConfidenceThreshold } from '@/types/deployment'
import { Label } from '@/types/label'

const DEFAULT_CONFIDENCE_THRESHOLD = 0.5

/**
 * Used for camera deployments where business logic is configured.
 * Extracts only the labels that are actually referenced in the logic rules,
 * so users only tune confidence for labels they care about.
 */
export const extractConfidenceThresholdsFromBusinessLogic = (
  initialConfidenceValues: ConfidenceThreshold[],
  normalizedLogics: NormalizedStreamBusinessLogic[]
): ConfidenceThreshold[] => {
  const uniqueLabelIds = new Set<string>()

  // Function to extract label IDs from a single rule
  const extractLabelIdsFromRule = (rule: NormalizedBusinessLogicRule) => {
    rule.label_groups.forEach((group) => {
      uniqueLabelIds.add(group.label_id)

      group.sub_label_ids.forEach((sub_label_id) => {
        uniqueLabelIds.add(sub_label_id)
      })

      if (group.classification_label_id) {
        uniqueLabelIds.add(group.classification_label_id)
      }
    })
  }

  // Function to extract label IDs from a list of rules
  const extractLabelIdsFromRules = (rules: NormalizedBusinessLogicRule[]) => {
    rules.forEach(extractLabelIdsFromRule)
  }

  // Iterate over the parameters and extract label IDs from each rule
  normalizedLogics.forEach((logic) => {
    logic.parameters.forEach((parameter) => {
      extractLabelIdsFromRules(parameter.rules)
    })
  })

  // Convert the unique label IDs to confidence thresholds
  return Array.from(uniqueLabelIds).map((labelId) => {
    const existingConfidence = initialConfidenceValues.find(
      (confidence) => confidence.label_id === labelId
    )
    return {
      label_id: labelId,
      threshold: existingConfidence
        ? existingConfidence.threshold
        : DEFAULT_CONFIDENCE_THRESHOLD
    }
  })
}

/**
 * Used for media folder deployments where business logic is not configured.
 * Since there are no logic rules to derive labels from, we fall back to all
 * labels available on the selected models.
 */
export const extractConfidenceThresholdsFromLabels = (
  initialConfidenceValues: ConfidenceThreshold[],
  labels: Label[]
): ConfidenceThreshold[] => {
  return labels.map((label) => {
    const existing = initialConfidenceValues.find(
      (c) => c.label_id === label.id
    )
    return {
      label_id: label.id,
      threshold: existing ? existing.threshold : DEFAULT_CONFIDENCE_THRESHOLD
    }
  })
}
